import React from "react";
import { Link, Typography } from "@mui/material";
import styled from "@emotion/styled";
import TrendIndicator from "src/components/widgets/overview/tiles/TrendIndicator";
import { TOOLTIP_SECTION_LABEL_MAX_WIDTH, TOOLTIP_SECTION_ROW_LINK_TEST_ID } from "./HyroTooltipSection.consts";

// rows: [{ label, value, href?, delta? }]. Rendered as a fragment so the parent's column gap
// spaces the divider and section like any other tooltip line.
const HyroTooltipSection = ({ title, rows = [], divider = true }) => (
  <>
    {divider && <Divider />}
    <Section>
      <Typography variant="tooltip" color="text.600">{title}</Typography>
      {rows.map((row, rowIndex) => (
        // keyed by position, not label — row labels can repeat (two agents may share a name)
        <SectionRow key={`${title}-${rowIndex}`}>
          {row.href ? (
            <SectionLabelLink
              data-testid={TOOLTIP_SECTION_ROW_LINK_TEST_ID}
              variant="tooltip"
              color="text.700"
              href={row.href}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {row.label}
            </SectionLabelLink>
          ) : (
            <SectionLabel variant="tooltip" color="text.700">{row.label}</SectionLabel>
          )}
          <SectionValueGroup>
            <Typography variant="tooltip" color="text.700">{row.value}</Typography>
            {row.delta && <TrendIndicator {...row.delta} showPeriod={false} />}
          </SectionValueGroup>
        </SectionRow>
      ))}
    </Section>
  </>
);

const Divider = styled("div")(({ theme }) => ({
  height: 1,
  width: "100%",
  backgroundColor: theme.palette.system[200]
}));

const Section = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1)
}));

const SectionRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2)
}));

// caps only the label, so a long row label (e.g. an agent name) wraps instead of stretching the
// whole tooltip. anywhere lets a single over-long token break too. Labels below the cap are unaffected.
const SectionLabel = styled(Typography)({
  maxWidth: TOOLTIP_SECTION_LABEL_MAX_WIDTH,
  overflowWrap: "anywhere"
});

const SectionLabelLink = styled(Link)({
  maxWidth: TOOLTIP_SECTION_LABEL_MAX_WIDTH,
  overflowWrap: "anywhere"
});

const SectionValueGroup = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flexShrink: 0,
  whiteSpace: "nowrap"
}));

export default HyroTooltipSection;
